import { useForm } from "@tanstack/react-form";
import { useStore } from "@tanstack/react-store";
import { useEffect, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { useAnnounce } from "@/hooks/use-announcer";
import { track } from "../../analytics";
import { useSnoozeSurvey, useSubmitSurvey } from "./api";
import { ChoiceQuestion } from "./ChoiceQuestion";
import { MultiChoiceQuestion } from "./MultiChoiceQuestion";
import type {
  Audience,
  SurveyOption,
  SurveyState,
  SurveySubmission,
} from "./types";

const FOLLOW_UPS_ID = "survey-follow-ups";

const YES_NO: SurveyOption[] = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

/** The radio groups speak strings; these keep the boolean<->string edge in one place. */
const toChoice = (value: boolean | null) =>
  value === null ? "" : value ? "yes" : "no";
const fromChoice = (value: string) => (value === "" ? null : value === "yes");

/** Mirrors the backend rules in books/onboarding.py, so the event we track is not a guess. */
function clusterFor(
  audience: Audience,
  wroteInteractiveFiction: boolean | null,
) {
  return audience === "organisation" || wroteInteractiveFiction
    ? "pro"
    : "base";
}

interface OnboardingSurveyFormProps {
  state: SurveyState;
  /** Genre names, already loaded — the shell waits for them so hydration cannot race. */
  genreNames: string[];
  source: "auto" | "menu";
  onDone: () => void;
}

export function OnboardingSurveyForm({
  state,
  genreNames,
  source,
  onDone,
}: OnboardingSurveyFormProps) {
  const announce = useAnnounce();
  const submit = useSubmitSurvey();
  const snooze = useSnoozeSurvey();
  const isPending = submit.isPending || snooze.isPending;

  const genreOptions = useMemo<SurveyOption[]>(
    () =>
      [...genreNames, ...state.options.extraGenres].map((name) => ({
        value: name,
        label: name,
      })),
    [genreNames, state.options.extraGenres],
  );

  const form = useForm({
    defaultValues: {
      audience: state.answers.audience,
      isAuthor: toChoice(state.answers.isAuthor),
      // Q3 stores genre *names*, so a renamed Genre leaves a stored value with no checkbox
      // to render it. Drop those on hydration rather than silently resubmitting them.
      genres: state.answers.genres.filter((genre) =>
        genreOptions.some((option) => option.value === genre),
      ),
      wroteInteractiveFiction: toChoice(state.answers.wroteInteractiveFiction),
      publishedOn: state.answers.publishedOn,
    },
    onSubmit: ({ value }) => {
      const isAuthor = value.isAuthor === "yes";
      const wroteInteractiveFiction = isAuthor
        ? fromChoice(value.wroteInteractiveFiction)
        : null;
      const payload: SurveySubmission = {
        audience: value.audience as Audience,
        isAuthor,
        genres: isAuthor ? value.genres : [],
        wroteInteractiveFiction,
        publishedOn: isAuthor ? value.publishedOn : [],
      };

      submit.mutate(payload, {
        onSuccess: () => {
          track("user_survey_submitted", {
            source,
            cluster: clusterFor(payload.audience, wroteInteractiveFiction),
            audience: payload.audience,
            is_author: isAuthor,
            wrote_interactive_fiction: wroteInteractiveFiction,
            genre_count: payload.genres.length,
            published_on_count: payload.publishedOn.length,
          });
          announce("Thanks — your answers have been saved.", {
            politeness: "assertive",
          });
          onDone();
        },
      });
    },
  });

  const isAuthor = useStore(form.store, (s) => s.values.isAuthor);
  const showFollowUps = isAuthor === "yes";

  // Announce, never move focus: Radix radio groups select on arrow-key *focus*, so walking
  // from "No" to "Yes" fires onValueChange for each item passed. Throttled for the same
  // reason. The first render is skipped so re-opening an answered survey stays quiet.
  const previousShowFollowUps = useRef(showFollowUps);
  useEffect(() => {
    if (previousShowFollowUps.current === showFollowUps) return;
    previousShowFollowUps.current = showFollowUps;
    announce(
      showFollowUps
        ? "Three more questions added below."
        : "Follow-up questions removed.",
      { channel: "survey-follow-ups", throttleMs: 500 },
    );
  }, [showFollowUps, announce]);

  const onSnooze = () => {
    snooze.mutate(undefined, {
      onSuccess: (result) => {
        track("user_survey_snoozed", {
          source,
          snooze_count: result.snoozeCount,
        });
        announce(
          `No problem — we will ask again in ${result.snoozeDays} days.`,
          { politeness: "assertive" },
        );
        onDone();
      },
    });
  };

  const error = submit.error ?? snooze.error;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        form.handleSubmit();
      }}
      className="flex flex-col gap-6"
    >
      <form.Field
        name="audience"
        validators={{
          onSubmit: ({ value }) =>
            value ? undefined : "Pick one to continue.",
        }}
      >
        {(field) => (
          <ChoiceQuestion
            question="Are you here as an individual, or on behalf of an organisation?"
            options={state.options.audience}
            value={field.state.value}
            onChange={(value) => field.handleChange(value as Audience)}
            error={field.state.meta.errors[0] as string | undefined}
          />
        )}
      </form.Field>

      <form.Field
        name="isAuthor"
        validators={{
          onSubmit: ({ value }) =>
            value ? undefined : "Pick one to continue.",
        }}
      >
        {(field) => (
          <ChoiceQuestion
            question="Are you a writer, game designer, or similar?"
            options={YES_NO}
            value={field.state.value}
            onChange={(value) => {
              field.handleChange(value);
              if (value === "no") {
                // Cleared, not merely hidden: a stale answer here would be submitted and
                // would reach Mixpanel as a genre this person never claimed.
                form.setFieldValue("genres", []);
                form.setFieldValue("wroteInteractiveFiction", "");
                form.setFieldValue("publishedOn", []);
              }
            }}
            controls={FOLLOW_UPS_ID}
            error={field.state.meta.errors[0] as string | undefined}
          />
        )}
      </form.Field>

      <div id={FOLLOW_UPS_ID}>
        {showFollowUps && (
          <div
            role="group"
            aria-label="Questions for writers"
            className="flex flex-col gap-6"
          >
            <form.Field name="genres">
              {(field) => (
                <MultiChoiceQuestion
                  question="What genres do you usually write?"
                  hint="Select all that apply, or none if you would rather not say."
                  options={genreOptions}
                  values={field.state.value}
                  onChange={field.handleChange}
                />
              )}
            </form.Field>

            <form.Field
              name="wroteInteractiveFiction"
              validators={{
                // Guarded on Q2 rather than relying on the field being unmounted: the form
                // keeps values and validators for conditionally-rendered fields, so an
                // unguarded rule here would block a non-author from ever submitting.
                onSubmit: ({ value, fieldApi }) =>
                  fieldApi.form.getFieldValue("isAuthor") !== "yes" || value
                    ? undefined
                    : "Pick one to continue.",
              }}
            >
              {(field) => (
                <ChoiceQuestion
                  question="Have you ever written interactive fiction?"
                  options={YES_NO}
                  value={field.state.value}
                  onChange={field.handleChange}
                  error={field.state.meta.errors[0] as string | undefined}
                />
              )}
            </form.Field>

            <form.Field
              name="publishedOn"
              validators={{
                onSubmit: ({ value, fieldApi }) =>
                  fieldApi.form.getFieldValue("isAuthor") !== "yes" ||
                  value.length
                    ? undefined
                    : "Select at least one option.",
              }}
            >
              {(field) => (
                <MultiChoiceQuestion
                  question="Have you ever published your work?"
                  hint="Select all that apply."
                  options={state.options.publishedOn}
                  values={field.state.value}
                  onChange={field.handleChange}
                  exclusiveValue="none"
                  error={field.state.meta.errors[0] as string | undefined}
                />
              )}
            </form.Field>
          </div>
        )}
      </div>

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error.message || "Something went wrong. Please try again."}
        </p>
      )}

      <DialogFooter>
        {/* Snoozing only means something for the prompt we opened unbidden. Someone who
            picked "About you" out of the menu came here on purpose — there is nothing to
            put off, so they get a plain way back out instead. */}
        {source === "auto" ? (
          <Button
            type="button"
            variant="outline"
            onClick={onSnooze}
            disabled={isPending}
          >
            Remind me in {state.snoozeDays} days
          </Button>
        ) : (
          <Button
            type="button"
            variant="outline"
            onClick={onDone}
            disabled={isPending}
          >
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={isPending}>
          Save answers
        </Button>
      </DialogFooter>
    </form>
  );
}
